// src/utils/hooks.js
// Shared Custom Hooks for Admin Pages - Production Ready

import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * useDebounce Hook
 * Debounces a value to prevent excessive updates
 * 
 * @param {any} value - The value to debounce
 * @param {number} delay - Delay in milliseconds (default: 300)
 * @returns {any} The debounced value
 * 
 * @example
 * const [searchTerm, setSearchTerm] = useState('');
 * const debouncedSearch = useDebounce(searchTerm, 500);
 * 
 * useEffect(() => {
 *   if (debouncedSearch) {
 *     searchUsers(debouncedSearch);
 *   }
 * }, [debouncedSearch]);
 */
export const useDebounce = (value, delay = 300) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

/**
 * useAsync Hook
 * Manages async operations with loading, error, and data states
 * 
 * @param {Function} asyncFunction - The async function to execute
 * @param {boolean} immediate - Whether to execute immediately (default: false)
 * @returns {Object} { loading, error, data, execute, reset }
 * 
 * @example
 * const { loading, error, data, execute } = useAsync(fetchUsers);
 * 
 * useEffect(() => {
 *   execute();
 * }, []);
 */
export const useAsync = (asyncFunction, immediate = false) => {
  const [state, setState] = useState({
    loading: immediate,
    error: null,
    data: null,
  });

  const execute = useCallback(
    async (...params) => {
      setState({ loading: true, error: null, data: null });
      try {
        const data = await asyncFunction(...params);
        setState({ loading: false, error: null, data });
        return data;
      } catch (error) {
        setState({ loading: false, error, data: null });
        throw error;
      }
    },
    [asyncFunction]
  );

  const reset = useCallback(() => {
    setState({ loading: false, error: null, data: null });
  }, []);

  useEffect(() => {
    if (immediate) {
      execute();
    }
  }, [execute, immediate]);

  return { ...state, execute, reset };
};

/**
 * usePagination Hook
 * Manages pagination state and calculations
 * 
 * @param {number} totalItems - Total number of items
 * @param {number} itemsPerPage - Items per page (default: 10)
 * @returns {Object} Pagination state and functions
 * 
 * @example
 * const pagination = usePagination(users.length, 15);
 * 
 * const currentUsers = users.slice(
 *   pagination.offset,
 *   pagination.offset + pagination.itemsPerPage
 * );
 */
export const usePagination = (totalItems, itemsPerPage = 10) => {
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const offset = (currentPage - 1) * itemsPerPage;

  const goToPage = useCallback(
    (page) => {
      const pageNumber = Math.max(1, Math.min(page, totalPages));
      setCurrentPage(pageNumber);
    },
    [totalPages]
  );

  const nextPage = useCallback(() => {
    goToPage(currentPage + 1);
  }, [currentPage, goToPage]);

  const prevPage = useCallback(() => {
    goToPage(currentPage - 1);
  }, [currentPage, goToPage]);

  const reset = useCallback(() => {
    setCurrentPage(1);
  }, []);

  // Reset to page 1 when totalItems changes significantly
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  return {
    currentPage,
    totalPages,
    itemsPerPage,
    offset,
    hasNextPage: currentPage < totalPages,
    hasPrevPage: currentPage > 1,
    goToPage,
    nextPage,
    prevPage,
    reset,
  };
};

/**
 * useLocalStorage Hook
 * Syncs state with localStorage
 * 
 * @param {string} key - localStorage key
 * @param {any} initialValue - Initial value if key doesn't exist
 * @returns {Array} [value, setValue, removeValue]
 * 
 * @example
 * const [drafts, setDrafts, removeDrafts] = useLocalStorage('emailDrafts', []);
 */
export const useLocalStorage = (key, initialValue) => {
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.error(`Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  });

  const setValue = useCallback(
    (value) => {
      try {
        const valueToStore = value instanceof Function ? value(storedValue) : value;
        setStoredValue(valueToStore);
        window.localStorage.setItem(key, JSON.stringify(valueToStore));
      } catch (error) {
        console.error(`Error setting localStorage key "${key}":`, error);
      }
    },
    [key, storedValue]
  );

  const removeValue = useCallback(() => {
    try {
      window.localStorage.removeItem(key);
      setStoredValue(initialValue);
    } catch (error) {
      console.error(`Error removing localStorage key "${key}":`, error);
    }
  }, [key, initialValue]);

  return [storedValue, setValue, removeValue];
};

/**
 * useAutoSave Hook
 * Auto-saves data to localStorage with debouncing
 * 
 * @param {string} key - localStorage key
 * @param {any} data - Data to save
 * @param {number} delay - Debounce delay (default: 1000ms)
 * 
 * @example
 * const [emailBody, setEmailBody] = useState('');
 * useAutoSave('emailDraft', emailBody, 2000);
 */
export const useAutoSave = (key, data, delay = 1000) => {
  const [lastSaved, setLastSaved] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setIsSaving(true);
    const handler = setTimeout(() => {
      try {
        window.localStorage.setItem(key, JSON.stringify(data));
        setLastSaved(new Date());
        setIsSaving(false);
      } catch (error) {
        console.error('Auto-save error:', error);
        setIsSaving(false);
      }
    }, delay);

    return () => clearTimeout(handler);
  }, [key, data, delay]);

  return { lastSaved, isSaving };
};

/**
 * useRateLimiter Hook
 * Limits the rate of function calls
 * 
 * @param {number} limit - Maximum number of calls
 * @param {number} window - Time window in milliseconds (default: 60000)
 * @returns {Function} checkRateLimit function
 * 
 * @example
 * const checkRateLimit = useRateLimiter(5, 60000);
 * 
 * const handleSubmit = () => {
 *   try {
 *     checkRateLimit();
 *     submitForm();
 *   } catch (error) {
 *     toast({ title: error.message, status: 'warning' });
 *   }
 * };
 */
export const useRateLimiter = (limit = 5, window = 60000) => {
  const attempts = useRef([]);

  const checkRateLimit = useCallback(() => {
    const now = Date.now();
    
    // Remove old attempts outside the window
    attempts.current = attempts.current.filter(time => now - time < window);
    
    if (attempts.current.length >= limit) {
      const oldestAttempt = attempts.current[0];
      const timeUntilReset = Math.ceil((window - (now - oldestAttempt)) / 1000);
      throw new Error(
        `Rate limit exceeded. Please try again in ${timeUntilReset} seconds.`
      );
    }
    
    attempts.current.push(now);
  }, [limit, window]);

  return checkRateLimit;
};

/**
 * useToggle Hook
 * Manages boolean state with toggle function
 * 
 * @param {boolean} initialValue - Initial boolean value (default: false)
 * @returns {Array} [value, toggle, setTrue, setFalse]
 * 
 * @example
 * const [isOpen, toggleOpen, open, close] = useToggle();
 */
export const useToggle = (initialValue = false) => {
  const [value, setValue] = useState(initialValue);

  const toggle = useCallback(() => {
    setValue(v => !v);
  }, []);

  const setTrue = useCallback(() => {
    setValue(true);
  }, []);

  const setFalse = useCallback(() => {
    setValue(false);
  }, []);

  return [value, toggle, setTrue, setFalse];
};

/**
 * useForm Hook
 * Manages form state with validation
 * 
 * @param {Object} initialValues - Initial form values
 * @param {Function} validationSchema - Validation function
 * @returns {Object} Form state and handlers
 * 
 * @example
 * const form = useForm(
 *   { email: '', password: '' },
 *   (values) => {
 *     const errors = {};
 *     if (!values.email) errors.email = 'Required';
 *     return errors;
 *   }
 * );
 */
export const useForm = (initialValues = {}, validationSchema = null) => {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setValues(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  }, []);

  const handleBlur = useCallback((e) => {
    const { name } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
  }, []);

  const validate = useCallback(() => {
    if (!validationSchema) return {};
    
    try {
      const validationErrors = validationSchema(values);
      setErrors(validationErrors || {});
      return validationErrors || {};
    } catch (error) {
      console.error('Validation error:', error);
      return {};
    }
  }, [values, validationSchema]);

  const handleSubmit = useCallback(
    (onSubmit) => async (e) => {
      e?.preventDefault();
      
      setIsSubmitting(true);
      const validationErrors = validate();
      
      if (Object.keys(validationErrors).length === 0) {
        try {
          await onSubmit(values);
        } catch (error) {
          console.error('Form submission error:', error);
        }
      }
      
      setIsSubmitting(false);
    },
    [values, validate]
  );

  const reset = useCallback(() => {
    setValues(initialValues);
    setErrors({});
    setTouched({});
    setIsSubmitting(false);
  }, [initialValues]);

  const setFieldValue = useCallback((name, value) => {
    setValues(prev => ({ ...prev, [name]: value }));
  }, []);

  const setFieldError = useCallback((name, error) => {
    setErrors(prev => ({ ...prev, [name]: error }));
  }, []);

  // Validate on values change
  useEffect(() => {
    if (Object.keys(touched).length > 0) {
      validate();
    }
  }, [values, touched, validate]);

  return {
    values,
    errors,
    touched,
    isSubmitting,
    handleChange,
    handleBlur,
    handleSubmit,
    reset,
    setFieldValue,
    setFieldError,
    isValid: Object.keys(errors).length === 0,
  };
};

/**
 * useWindowSize Hook
 * Tracks window dimensions
 * 
 * @returns {Object} { width, height, isMobile, isTablet, isDesktop }
 * 
 * @example
 * const { isMobile } = useWindowSize();
 * 
 * return isMobile ? <MobileView /> : <DesktopView />;
 */
export const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return {
    ...windowSize,
    isMobile: windowSize.width < 768,
    isTablet: windowSize.width >= 768 && windowSize.width < 1024,
    isDesktop: windowSize.width >= 1024,
  };
};

/**
 * useClickOutside Hook
 * Detects clicks outside of a ref element
 * 
 * @param {Function} callback - Function to call on outside click
 * @returns {Object} ref - Ref to attach to element
 * 
 * @example
 * const ref = useClickOutside(() => setIsOpen(false));
 * return <div ref={ref}>Content</div>;
 */
export const useClickOutside = (callback) => {
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        callback();
      }
    };

    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [callback]);

  return ref;
};

/**
 * useInterval Hook
 * Declarative interval with cleanup
 * 
 * @param {Function} callback - Function to call on interval
 * @param {number} delay - Delay in milliseconds (null to pause)
 * 
 * @example
 * useInterval(() => {
 *   fetchLatestData();
 * }, 5000);
 */
export const useInterval = (callback, delay) => {
  const savedCallback = useRef();

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    if (delay === null) return;

    const tick = () => {
      savedCallback.current();
    };

    const id = setInterval(tick, delay);
    return () => clearInterval(id);
  }, [delay]);
};

/**
 * useMediaQuery Hook
 * Tracks media query matches
 * 
 * @param {string} query - Media query string
 * @returns {boolean} Whether the query matches
 * 
 * @example
 * const isLargeScreen = useMediaQuery('(min-width: 1024px)');
 */
export const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(
    () => window.matchMedia(query).matches
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia(query);
    const handler = (e) => setMatches(e.matches);
    
    mediaQuery.addListener(handler);
    return () => mediaQuery.removeListener(handler);
  }, [query]);

  return matches;
};